<template>
  <header class="bg-white dark:bg-gray-900 shadow-md sticky top-0 z-50 transition-colors duration-200">
    <div class="container mx-auto px-4">
      <div class="flex items-center justify-between h-16">
        <!-- Logo -->
        <router-link to="/" class="flex items-center space-x-3">
          <img 
            :src="isDark ? logoDark : logoLight"
            alt="Walabi"
            class="w-10 h-10"
          >
          <span class="text-xl font-bold text-gray-900 dark:text-white">Walabi</span>
        </router-link>

        <!-- Search Bar (Desktop) -->
        <div class="hidden md:flex flex-1 max-w-lg mx-8">
          <div class="relative w-full">
            <span class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-primary-500 dark:text-primary-400">
              <SearchAiIcon icon-class="h-5 w-5" />
            </span>
            <input
              v-model="searchQuery"
              @input="debouncedSearch"
              @keyup.enter="handleSearch"
              type="text"
              placeholder="Buscar productos..."
              class="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            >
          </div>
        </div>

        <!-- Navigation Links -->
        <nav class="hidden lg:flex items-center space-x-6">
          <router-link 
            to="/products" 
            class="text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            Mi Tienda
          </router-link>
        </nav>

        <!-- User Actions (Desktop) -->
        <div class="hidden md:flex items-center space-x-4">
          <!-- Currency Selector -->
          <div class="relative ml-4">
            <select
              v-model="selectedCurrency"
              @change="changeCurrency"
              class="appearance-none bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-1 pr-8 text-sm text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              <option value="PYG">Gs. Guaraníes</option>
              <option value="USD">$ Dólares</option>
            </select>
            <ChevronDownIcon class="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>

          <!-- Dark Mode Toggle -->
          <button
            @click="toggleDarkMode"
            class="p-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            title="Cambiar tema"
          >
            <SunIcon v-if="isDark" class="w-5 h-5" />
            <MoonIcon v-else class="w-5 h-5" />
          </button>

          <!-- Favorites Button -->
          <router-link 
            to="/favorites" 
            class="relative p-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            <HeartIcon class="w-6 h-6" />
            <span 
              v-if="favoritesStore.favoriteCount > 0"
              class="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center"
            >
              {{ favoritesStore.favoriteCount }}
            </span>
          </router-link>

          <!-- Compare Button -->
          <router-link 
            to="/compare" 
            class="relative p-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            <ScaleIcon class="w-6 h-6" />
            <span 
              v-if="compareStore.compareCount > 0"
              class="absolute -top-1 -right-1 bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center"
            >
              {{ compareStore.compareCount }}
            </span>
          </router-link>

          <!-- Cart Button -->
          <router-link 
            to="/cart" 
            class="relative p-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
          >
            <ShoppingCartIcon class="w-6 h-6" />
            <span 
              v-if="cartStore.itemCount > 0"
              class="absolute -top-1 -right-1 bg-primary-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center"
            >
              {{ cartStore.itemCount }}
            </span>
          </router-link>

          <!-- User Menu -->
          <div v-if="authStore.isAuthenticated" class="relative">
            <button 
              @click="toggleUserDropdown"
              @blur="handleUserDropdownBlur"
              class="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors focus:outline-none"
            >
              <!-- User Avatar -->
              <div class="w-8 h-8 rounded-full overflow-hidden bg-primary-600 flex items-center justify-center">
                <img
                  v-if="authStore.user?.avatar"
                  :src="authStore.user.avatar"
                  :alt="`${authStore.user.firstName} ${authStore.user.lastName}`"
                  class="w-full h-full object-cover"
                  @error="onAvatarError"
                >
                <UserIcon v-else class="w-5 h-5 text-white" />
              </div>
              <ChevronDownIcon class="w-4 h-4 text-gray-600 dark:text-gray-400" />
            </button>
            <div 
              v-show="showUserDropdown"
              @mouseenter="showUserDropdown = true"
              @mouseleave="showUserDropdown = false"
              class="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700 z-50"
            >
              <div class="py-1">
                <div class="px-4 py-2 text-sm text-gray-900 dark:text-white border-b border-gray-200 dark:border-gray-700">
                  {{ authStore.user?.firstName }} {{ authStore.user?.lastName }}
                </div>
                <router-link 
                  to="/profile" 
                  @click="showUserDropdown = false"
                  class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Mi Perfil
                </router-link>
                <router-link 
                  to="/orders" 
                  @click="showUserDropdown = false"
                  class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Mis Pedidos
                </router-link>
                <router-link 
                  to="/favorites" 
                  @click="showUserDropdown = false"
                  class="block px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Mis Favoritos
                </router-link>
                <button 
                  @click="handleLogout" 
                  class="block w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Cerrar Sesión
                </button>
              </div>
            </div>
          </div>

          <!-- Login Button -->
          <router-link 
            v-else
            to="/login" 
            class="bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Iniciar Sesión
          </router-link>

          <!-- Mobile Menu Button -->
          <button 
            @click="toggleMobileMenu"
            class="lg:hidden p-2 text-gray-700 dark:text-gray-300 focus:outline-none"
          >
            <Bars3Icon v-if="!showMobileMenu" class="w-6 h-6" />
            <XMarkIcon v-else class="w-6 h-6" />
          </button>
        </div>

        <!-- Mobile Actions Row -->
        <div class="md:hidden flex items-center space-x-2">
          <!-- Dark Mode Toggle -->
          <button
            @click="toggleDarkMode"
            class="p-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
            title="Cambiar tema"
          >
            <SunIcon v-if="isDark" class="w-5 h-5" />
            <MoonIcon v-else class="w-5 h-5" />
          </button>

          <!-- Mobile Menu Button -->
          <button 
            @click="toggleMobileMenu"
            class="p-2 text-gray-700 dark:text-gray-300 focus:outline-none"
          >
            <Bars3Icon v-if="!showMobileMenu" class="w-6 h-6" />
            <XMarkIcon v-else class="w-6 h-6" />
          </button>
        </div>
      </div>

      <!-- Mobile Search -->
      <div class="md:hidden pb-4">
        <div class="relative">
          <span class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-primary-500 dark:text-primary-400">
            <SearchAiIcon icon-class="h-5 w-5" />
          </span>
          <input
            v-model="searchQuery"
            @input="debouncedSearch"
            @keyup.enter="handleSearch"
            type="text"
            placeholder="Buscar productos..."
            class="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
          >
        </div>
      </div>
    </div>

    <!-- Mobile Menu -->
    <div 
      v-show="showMobileMenu" 
      class="lg:hidden bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700"
    >
      <div class="container mx-auto px-4 py-4">
        <nav class="space-y-4">
          <!-- Main Navigation -->
          <router-link 
            to="/products" 
            class="block text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors py-2 text-lg font-medium"
            @click="closeMobileMenu"
          >
            Mi Tienda
          </router-link>

          <!-- Action Items -->
          <div class="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
            <!-- Favorites -->
            <router-link 
              to="/favorites" 
              class="flex items-center justify-between py-3 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              @click="closeMobileMenu"
            >
              <div class="flex items-center space-x-3">
                <HeartIcon class="w-6 h-6" />
                <span class="text-lg font-medium">Favoritos</span>
              </div>
              <span 
                v-if="favoritesStore.favoriteCount > 0"
                class="bg-red-500 text-white text-sm rounded-full w-6 h-6 flex items-center justify-center"
              >
                {{ favoritesStore.favoriteCount }}
              </span>
            </router-link>

            <!-- Compare -->
            <router-link 
              to="/compare" 
              class="flex items-center justify-between py-3 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              @click="closeMobileMenu"
            >
              <div class="flex items-center space-x-3">
                <ScaleIcon class="w-6 h-6" />
                <span class="text-lg font-medium">Comparar</span>
              </div>
              <span 
                v-if="compareStore.compareCount > 0"
                class="bg-blue-500 text-white text-sm rounded-full w-6 h-6 flex items-center justify-center"
              >
                {{ compareStore.compareCount }}
              </span>
            </router-link>

            <!-- Cart -->
            <router-link 
              to="/cart" 
              class="flex items-center justify-between py-3 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
              @click="closeMobileMenu"
            >
              <div class="flex items-center space-x-3">
                <ShoppingCartIcon class="w-6 h-6" />
                <span class="text-lg font-medium">Carrito</span>
              </div>
              <span 
                v-if="cartStore.itemCount > 0"
                class="bg-primary-600 text-white text-sm rounded-full w-6 h-6 flex items-center justify-center"
              >
                {{ cartStore.itemCount }}
              </span>
            </router-link>

            <!-- Login/User Section -->
            <div class="border-t border-gray-200 dark:border-gray-700 pt-4">
              <template v-if="!authStore.isAuthenticated">
                <router-link 
                  to="/login" 
                  class="flex items-center space-x-3 py-3 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  @click="closeMobileMenu"
                >
                  <UserIcon class="w-6 h-6" />
                  <span class="text-lg font-medium">Ingresar</span>
                </router-link>
              </template>

              <template v-else>
                <div class="space-y-3">
                  <div class="flex items-center space-x-3 py-2">
                    <!-- Mobile User Avatar -->
                    <div class="w-8 h-8 rounded-full overflow-hidden bg-primary-600 flex items-center justify-center">
                      <img
                        v-if="authStore.user?.avatar"
                        :src="authStore.user.avatar"
                        :alt="`${authStore.user.firstName} ${authStore.user.lastName}`"
                        class="w-full h-full object-cover"
                        @error="onAvatarError"
                      >
                      <UserIcon v-else class="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p class="text-lg font-medium text-gray-900 dark:text-white">
                        {{ authStore.user?.firstName }} {{ authStore.user?.lastName }}
                      </p>
                      <p class="text-sm text-gray-600 dark:text-gray-400">{{ authStore.user?.email }}</p>
                    </div>
                  </div>
                  
                  <router-link 
                    to="/profile" 
                    class="block pl-11 py-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors" 
                    @click="closeMobileMenu"
                  >
                    Mi Perfil
                  </router-link>
                  
                  <router-link 
                    to="/orders" 
                    class="block pl-11 py-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors" 
                    @click="closeMobileMenu"
                  >
                    Mis Pedidos
                  </router-link>
                  
                  <button 
                    @click="handleLogout" 
                    class="block w-full text-left pl-11 py-2 text-gray-700 dark:text-gray-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                  >
                    Cerrar Sesión
                  </button>
                </div>
              </template>
            </div>
          </div>
        </nav>
      </div>
    </div>
  </header>
</template>

<script setup lang="ts">
import { ref, nextTick, computed, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useCartStore } from '@/stores/cart';
import { useFavoritesStore } from '@/stores/favorites';
import { useCompareStore } from '@/stores/compare';
import { useCurrencyStore } from '@/stores/currency';
import { useDarkMode } from '@/composables/useDarkMode';
import SearchAiIcon from '@/components/icons/SearchAiIcon.vue';
import {
  ShoppingCartIcon,
  UserIcon,
  ChevronDownIcon,
  Bars3Icon,
  XMarkIcon,
  SunIcon,
  MoonIcon,
  HeartIcon,
  ScaleIcon,
} from '@heroicons/vue/24/outline';

import logoLight from '@/assets/logo_mini_walabi.png';
import logoDark from '@/assets/logo_mini_walabi_dark.png';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const cartStore = useCartStore();
const favoritesStore = useFavoritesStore();
const compareStore = useCompareStore();
const currencyStore = useCurrencyStore();
const { isDark, toggleDarkMode } = useDarkMode();

const searchQuery = ref('');
const showMobileMenu = ref(false);
const showUserDropdown = ref(false);
const selectedCurrency = computed({
  get: () => currencyStore.currentCurrency,
  set: (value) => currencyStore.setCurrency(value),
});

let searchTimeout: ReturnType<typeof setTimeout> | undefined;

const syncSearchFromRoute = () => {
  searchQuery.value = typeof route.query.search === 'string' ? route.query.search : '';
};

const performSearch = async () => {
  const trimmedQuery = searchQuery.value.trim();

  if (route.name === 'Products') {
    const nextQuery = { ...route.query } as Record<string, string>;
    delete nextQuery.page;

    if (trimmedQuery) {
      nextQuery.search = trimmedQuery;
    } else {
      delete nextQuery.search;
    }

    await router.replace({
      name: 'Products',
      query: nextQuery,
    });
  } else if (trimmedQuery) {
    await router.push({
      name: 'Products',
      query: { search: trimmedQuery }
    });
  }

  closeMobileMenu();
};

const handleSearch = () => {
  clearTimeout(searchTimeout);
  performSearch();
};

const debouncedSearch = () => {
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(() => {
    performSearch();
  }, 500);
};

const handleLogout = () => {
  authStore.logout();
  showUserDropdown.value = false;
  closeMobileMenu();
  router.push('/');
};

const toggleMobileMenu = () => {
  showMobileMenu.value = !showMobileMenu.value;
};

const closeMobileMenu = () => {
  showMobileMenu.value = false;
};

const changeCurrency = () => {
  currencyStore.setCurrency(selectedCurrency.value);
};

const toggleUserDropdown = () => {
  showUserDropdown.value = !showUserDropdown.value;
};

const handleUserDropdownBlur = () => {
  nextTick(() => {
    setTimeout(() => {
      showUserDropdown.value = false;
    }, 150);
  });
};

// Handle avatar loading errors
const onAvatarError = (event: Event) => {
  const img = event.target as HTMLImageElement;
  // Hide the image and let the fallback icon show
  img.style.display = 'none';
};

watch(() => route.query.search, () => {
  syncSearchFromRoute();
}, { immediate: true });

// Close dropdowns when clicking outside
if (typeof window !== 'undefined') {
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    
    // Close mobile menu if clicking outside
    if (showMobileMenu.value && !target.closest('header')) {
      closeMobileMenu();
    }
    
    // Close dropdowns if clicking outside
    if (!target.closest('.relative')) {
      showUserDropdown.value = false;
    }
  });
}
</script>
